import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime, Duration } from 'luxon'

import '../../../styling/SignPn.scss'
import SigningContext from '../../../contexts/SigningContext'
import {
	VideoIdOtpSigningStep,
	VideoIdSigningOtpMethod,
	VideoVerificationStatus,
} from '../../../constants/videoIdValues'
import {
	IVideoIdOtpSignComplete,
	IVideoIdOtpSignInit,
} from '../../../interfaces/i-videoid'
import httpClient from '../../../services/httpClient'
import { appInsights } from '../../../appInsights'
import SigningStatus from '../../../constants/signingStatuses'
import { VideoIdOtpMethodSelection } from './VideoIdOtpMethodSelection'
import { VideoIdOtpValidation } from './VideoIdOtpValidation'
import { VideoIdPendingSigning } from './VideoIdPendingSigning'

const POLL_INTERVAL_MS = 10_000
const MAX_DURATION_MS = Duration.fromObject({ minutes: 10 }).as('milliseconds')

export const VideoIdOtpSignBlock = () => {
	const { t } = useTranslation()
	const { signingRequest, setSigned, setError } = useContext(SigningContext)
	const signingRequestId = signingRequest?.id ?? ''

	const [selectedOtpMethod, setSelectedOtpMethod] =
		useState<VideoIdSigningOtpMethod>(VideoIdSigningOtpMethod.Unknown)
	const [isOtpRetry, setIsOtpRetry] = useState(false)
	const [timedOut, setTimedOut] = useState(false)
	const [otpCode, setOtpCode] = useState('')
	const [loading, setLoading] = useState(false)
	const [verificationTimeout, setVerificationTimeout] = useState(false)
	const [verificationRejected, setVerificationRejected] = useState(false)

	const intervalRef = useRef<NodeJS.Timeout | null>(null)

	const videoIdStepKey = `videoIdOtpSigningStep.${signingRequestId}`

	const saveSigningStep = (step: VideoIdOtpSigningStep) => {
		sessionStorage.setItem(videoIdStepKey, step)
	}

	const getSigningStepFromStorage = (): VideoIdOtpSigningStep | null => {
		const value = sessionStorage.getItem(videoIdStepKey)
		if (Object.values(VideoIdOtpSigningStep).includes(value as VideoIdOtpSigningStep)) {
			return value as VideoIdOtpSigningStep
		}
		return null
	}

	const [videoIdSigningStep, setVideoIdSigningStep] =
		useState<VideoIdOtpSigningStep>(
			() => getSigningStepFromStorage() ?? VideoIdOtpSigningStep.OtpMethodSelection,
		)

	const setStepAndSave = (step: VideoIdOtpSigningStep) => {
		setVideoIdSigningStep(step)
		saveSigningStep(step)
	}

	useEffect(() => {
		if (videoIdSigningStep !== VideoIdOtpSigningStep.PendingSigning) return
		if (verificationRejected) return // already rejected from pre-check, no need to poll

		const startTime = DateTime.now()

		const stopPolling = () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}
		}

		const checkSignStatus = async () => {
			try {
				const response =
					await httpClient.getSigningRequestByIdIncludingVideoIdVerificationStatus(
						signingRequestId,
					)

				if (
					response.status === SigningStatus.signed &&
					response.videoVerificationStatus ===
						VideoVerificationStatus.Accepted
				) {
					stopPolling()
					setSigned()
					setStepAndSave(VideoIdOtpSigningStep.Done)
					return
				}

				if (
					response.videoVerificationStatus ===
					VideoVerificationStatus.Rejected
				) {
					setVerificationRejected(true)
					stopPolling()
					return
				}
			} catch (exception) {
				console.error(
					'Error when trying to fetch signing status:',
					exception,
				)
				appInsights.trackException({ exception: exception as Error })
			}

			if (
				DateTime.now().diff(startTime).as('milliseconds') >
				MAX_DURATION_MS
			) {
				setVerificationTimeout(true)
				stopPolling()
			}
		}

		intervalRef.current = setInterval(checkSignStatus, POLL_INTERVAL_MS)
		checkSignStatus()

		return stopPolling
	}, [videoIdSigningStep])

	const checkIfRejected = async (): Promise<boolean> => {
		const response =
			await httpClient.getSigningRequestByIdIncludingVideoIdVerificationStatus(
				signingRequestId,
			)
		if (
			response.videoVerificationStatus ===
			VideoVerificationStatus.Rejected
		) {
			setVerificationRejected(true)
			setVideoIdSigningStep(VideoIdOtpSigningStep.PendingSigning)
			return true
		}
		return false
	}

	const signPnInit = async () => {
		setLoading(true)
		setError(undefined)
		try {
			if (await checkIfRejected()) return
			await httpClient.videoIdOtpSignInit({
				signingRequestId,
				signingMethod: selectedOtpMethod,
			} as IVideoIdOtpSignInit)
			// intentionally not saving to session storage: OtpValidation should only restore when navigating forward, not when reloading without a new code sent
			setVideoIdSigningStep(VideoIdOtpSigningStep.OtpValidation)
		} catch (exception) {
			appInsights.trackException({ exception: exception as Error })
			setError(new Error(t('VideoIdSendOtpError')))
		} finally {
			setLoading(false)
		}
	}

	const signPnComplete = async (otp: string) => {
		setLoading(true)
		setError(undefined)
		try {
			if (await checkIfRejected()) return
			await httpClient.videoIdOtpSignComplete({
				signingRequestId,
				otp,
			} as IVideoIdOtpSignComplete)
			setStepAndSave(VideoIdOtpSigningStep.PendingSigning)
		} catch (exception) {
			appInsights.trackException({ exception: exception as Error })
			setError(new Error(t('VideoIdSignError')))
		} finally {
			setLoading(false)
		}
	}

	const backAndRetry = () => {
		setIsOtpRetry(true)
		setStepAndSave(VideoIdOtpSigningStep.OtpMethodSelection)
	}

	switch (videoIdSigningStep) {
		case VideoIdOtpSigningStep.OtpMethodSelection:
			return (
				<VideoIdOtpMethodSelection
					loading={loading}
					isOtpRetry={isOtpRetry}
					selectedOtpMethod={selectedOtpMethod}
					onSelectMethod={setSelectedOtpMethod}
					onSendCode={signPnInit}
				/>
			)
		case VideoIdOtpSigningStep.OtpValidation:
			return (
				<VideoIdOtpValidation
					timedOut={timedOut}
					onTimeout={setTimedOut}
					otpCode={otpCode}
					setOtpCode={setOtpCode}
					loading={loading}
					selectedOtpMethod={selectedOtpMethod}
					signingRequest={signingRequest}
					onValidate={signPnComplete}
					onBack={backAndRetry}
				/>
			)
		case VideoIdOtpSigningStep.PendingSigning:
			return (
				<VideoIdPendingSigning
					verificationTimeout={verificationTimeout}
					verificationRejected={verificationRejected}
				/>
			)
		default:
			return null
	}
}
